import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import toast, { Toaster } from 'react-hot-toast';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import axios from '../../../../axios';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { DialogActions, Grid, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { ImageUpload } from '../../../components/DropZone/ImageUpload';
import createNFTUpload from '../../../../assets/createNFTUpload.png';
import { baseImageUrl } from '../../../utils/config';
import { CREATE_EXTRA, UPDATE_EXTRA } from '../../../api';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';

const getExtraErrorMessage = (error) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string') return message;
  if (message && typeof message === 'object') {
    return Object.entries(message)
      .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(' ') : errors}`)
      .join(' ');
  }
  return error?.message || 'Extra could not be saved.';
};

function CreateExtraModal({ open, handleClose, serviceData, retrieveService, selectedExtra }) {
  const [loading, setLoading] = useState(false);
  let formData = new FormData();
  const fetchAndCreateFile = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const fileName = url.substring(url.lastIndexOf('/') + 1);
    return new File([blob], fileName, { type: blob.type });
  };
  const schema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    order: Yup.number()
      .transform((value, originalValue) => originalValue === '' ? undefined : value)
      .typeError('Order must be a whole number')
      .integer('Order must be a whole number')
      .required('Order is required'),
    time_hrs: Yup.number('Time in hours is required!')
      .min(0, 'Invalid time')
      .required('Time in hours is required!')
      .nullable(),
    tool_tip: Yup.string().required('Additional info (Tooltip) is required!'),
    price: Yup.number('Price is required!')
      .min(0, 'Invalid price')
      .required('Price is required!')
      .nullable(),
  });
  const formik = useFormik({
    initialValues: {
      title: '',
      order: '',
      time_hrs: null,
      tool_tip: '',
      price: null,
      image_12: null,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true);
      let file1;
      if (typeof (values.image_12) == 'string') {
        try {
          const fileImg1 = await Promise(fetchAndCreateFile(values?.image_12));
          file1 = fileImg1;
        } catch (error) {
          console.error('Error occurred while downloading files:', error);
        }
      } else {
        file1 = values.image_12;
      }
      if (selectedExtra?.id != null) formData.append('id', selectedExtra.id);
      formData.append('title', values?.title);
      formData.append('order', values?.order);
      formData.append('time_hrs', values?.time_hrs);
      formData.append('tool_tip', values?.tool_tip);
      formData.append('price', values.price);
      formData.append('service', serviceData?.id);
      if (file1) {
        formData.append('image_12', file1);
      }
      if (!selectedExtra) {
        const dupObj = { ...values };
        dupObj.service = serviceData?.id;
        toast.promise(
          axios.post(`${CREATE_EXTRA}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          }),
          {
            loading: () => {
              return `Creating Extra!`;
            },
            success: (res) => {
              setLoading(false);
              setTimeout(() => {
                resetForm();
                handleClose();
                retrieveService();
              }, 1000);

              return res?.data?.message;
            },
            error: (err) => {
              setLoading(false);
              return getExtraErrorMessage(err);
            },
          }
        );
      } else {
        const dupObj = { ...values };
        dupObj.id = selectedExtra?.id;
        toast.promise(
          axios.put(`${UPDATE_EXTRA}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          }),
          {
            loading: () => {
              return `Updating Extra!`;
            },
            success: (res) => {
              setLoading(false);
              setTimeout(() => {
                resetForm();
                handleClose();
                retrieveService();
              }, 1000);

              return res?.data?.message;
            },
            error: (err) => {
              setLoading(false);
              return getExtraErrorMessage(err);
            },
          }
        );
      }
    },
  });

  const { errors, touched, resetForm, setFieldValue, handleSubmit, getFieldProps, values } = formik;
  useEffect(() => {
    if (selectedExtra) {
      setFieldValue('title', selectedExtra.title || '');
      setFieldValue('time_hrs', selectedExtra.time_hrs || null);
      setFieldValue('tool_tip', selectedExtra.tool_tip || '');
      setFieldValue('price', selectedExtra.price || null);
      setFieldValue('order', selectedExtra.order ?? '');
      setFieldValue('image_12', baseImageUrl + selectedExtra.image_12 || null);
    }
  }, [selectedExtra, setFieldValue]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        resetForm();
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { height: 'min(760px, calc(100vh - 40px))', display: 'flex', flexDirection: 'column', borderRadius: 3.5, overflow: 'hidden', boxShadow: '0 26px 80px rgba(15,35,70,.24)' } }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.4, bgcolor: '#f8fbff', borderBottom: '1px solid #e5edf6' }}>
        <Box sx={{ width: 42, height: 42, display: 'grid', placeItems: 'center', borderRadius: 2.5, bgcolor: '#e7f2ff', color: '#1976d2' }}><ExtensionOutlinedIcon /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}><Typography noWrap sx={{ color: '#10224a', fontSize: 19, fontWeight: 850 }}>{selectedExtra ? 'Update Extra' : 'Add Extra'}</Typography><Typography noWrap sx={{ mt: .2, color: '#718096', fontSize: 11 }}>{selectedExtra?.title || 'Create an optional add-on for this service.'}</Typography></Box>
        <IconButton size="small" aria-label="Close" onClick={() => { handleClose(); resetForm(); }}><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit} style={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: 'column', overflow: 'hidden' }}>
          <DialogContent sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: '24px !important' }}>
            <Grid container spacing={2}>
              <Grid item sm={12} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  type="text"
                  label="Title"
                  {...getFieldProps('title')}
                  error={Boolean(touched.title && errors.title)}
                  helperText={touched.title && errors.title}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item sm={12} xs={12} md={6}>
                <Tooltip title="Time is in decimal format. E.g.: 1.5 here actually means an hour and 30 minutes.">
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="Time in hours"
                    inputProps={{ min: 0 }}
                    {...getFieldProps('time_hrs')}
                    error={Boolean(touched.time_hrs && errors.time_hrs)}
                    helperText={touched.time_hrs && errors.time_hrs}
                    InputProps={{ endAdornment: <InputAdornment position="end">hrs</InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Tooltip>
              </Grid>

              <Grid item sm={12} xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="Cost"
                  inputProps={{ min: 0 }}
                  {...getFieldProps('price')}
                  error={Boolean(touched.price && errors.price)}
                  helperText={touched.price && errors.price}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  rows={5}
                  type="text"
                  label="Additional info (Tooltip)"
                  {...getFieldProps('tool_tip')}
                  error={Boolean(touched.tool_tip && errors.tool_tip)}
                  helperText={touched.tool_tip && errors.tool_tip}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="Order *"
                  inputProps={{ step: 1 }}
                  {...getFieldProps('order')}
                  error={Boolean(touched.order && errors.order)}
                  helperText={touched.order && errors.order}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, border: '1px solid #dce6f1', borderRadius: 2.5, bgcolor: '#fbfdff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><Box sx={{ width: 32, height: 32, display: 'grid', placeItems: 'center', borderRadius: 1.7, bgcolor: '#edf5ff', color: '#1976d2' }}><ImageRoundedIcon fontSize="small" /></Box><Box><Typography sx={{ color: '#183153', fontSize: 12, fontWeight: 800 }}>Extra Image</Typography><Typography sx={{ color: '#718096', fontSize: 10 }}>Shown beside this extra on the booking page. Maximum size 5 MB.</Typography></Box></Box>
                <ImageUpload
                  name="image_12"
                  empty={!values?.image_12}
                  Isvideo={false}
                  inputProps={{ accept: 'image/*' }}
                  src={values?.image_12 || createNFTUpload}
                  buttonProps={{
                    className: '!flex !justify-start !p-0 !py-4',
                  }}
                  onChange={(e, file) => {
                    setFieldValue(e, file);
                  }}
                  error={touched.image_12 && Boolean(errors.image_12)}
                  helperText={touched.image_12 && errors.image_12}
                />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ flexShrink: 0, px: 3, py: 1.8, borderTop: '1px solid #e5edf6', bgcolor: '#fbfdff' }}>
              <Button
                onClick={() => {
                  handleClose();
                  resetForm();
                }}
                variant="outlined"
                color="inherit"
                sx={{ minWidth: 94, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                Cancel
              </Button>
              <LoadingButton type="submit" color="primary" loading={loading} variant="contained" sx={{ minWidth: 126, borderRadius: 2, textTransform: 'none', fontWeight: 750 }}>
                {!selectedExtra ? 'Add Extra' : 'Save Changes'}
              </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
      <Toaster position="top-right" />
    </Dialog>
  );
}

export default CreateExtraModal;
